// Public 3-step signup wizard served on chitos.<apex>/signup.
//
// Step 1: pick plan (monthly/yearly toggle)
// Step 2: company details (business name, subdomain, email, optional phone)
// Step 3: payment summary → redirect to Razorpay hosted UPI AutoPay auth
//
// On return from Razorpay (chitos.<apex>/signup?ok=1&tenant_id=...), we poll
// platform.tenant_status until status flips from pending_payment → active
// and show the "Ready — check your email" screen. The QStash provision job
// fires from the subscription.authenticated webhook, NOT from this client.

import { useEffect, useState, type FormEvent, type ReactElement } from 'react';
import { Link, useSearchParams } from 'react-router';
import { orpc } from '../lib/orpc.js';
import { open_subscription_checkout } from '../lib/razorpay.js';
import { friendly_error } from '../lib/friendly-error.js';

type Stage = 'plan' | 'details' | 'pay' | 'resume_pay' | 'awaiting' | 'ready' | 'failed';
type BillingPeriod = 'monthly' | 'yearly';

interface Plan {
  id: string;
  tier: string;
  label: string;
  description: string;
  features: string[];
  monthly_price_paise: number;
  yearly_price_paise: number;
}

const APEX = import.meta.env.VITE_CHITOS_APEX_DOMAIN || 'localhost';

function format_inr(paise: number): string {
  return (paise / 100).toLocaleString('en-IN');
}

export function SignupPage(): ReactElement {
  const [params, set_params] = useSearchParams();
  const return_tenant_id = params.get('tenant_id');
  // ?ok=1&tenant_id=X is the post-Razorpay return — start polling immediately.
  // ?tenant_id=X alone means the tenant exists (mid-flow refresh after the
  // create step but before the modal confirmed payment) — resume payment by
  // reopening Razorpay against the existing subscription_id.
  const came_from_razorpay = params.get('ok') === '1';
  const initial_stage: Stage = return_tenant_id
    ? (came_from_razorpay ? 'awaiting' : 'resume_pay')
    : 'plan';

  const [stage, set_stage] = useState<Stage>(initial_stage);
  const [busy, set_busy] = useState(false);
  const [error, set_error] = useState<string | null>(null);

  // Step 1
  const [plans, set_plans] = useState<Plan[]>([]);
  const [plan_id, set_plan_id] = useState<string>('');
  const [billing_period, set_billing_period] = useState<BillingPeriod>('monthly');

  // Step 2
  const [business_name, set_business_name] = useState('');
  const [subdomain, set_subdomain] = useState('');
  const [email, set_email] = useState('');
  const [phone, set_phone] = useState('');
  const [accepts, set_accepts] = useState(false);
  const [sub_check, set_sub_check] = useState<{ ok: boolean; reason?: string } | null>(null);

  const [tenant_id, set_tenant_id] = useState<string>(return_tenant_id ?? '');
  const [resume_info, set_resume_info] = useState<{ subdomain: string; subscription_id: string } | null>(null);

  // ── Resume an in-flight signup. /signup?tenant_id=X without ?ok=1 means the
  //    tenant was created but Razorpay never confirmed (refresh, modal closed,
  //    studio error). Fetch status; if pending_payment + we have a subscription,
  //    show the resume UI; otherwise route to the matching stage.
  useEffect(() => {
    if (stage !== 'resume_pay' || !tenant_id) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await orpc.platform.tenant_status({ tenant_id });
        if (cancelled) return;
        if (r.status === 'active') return set_stage('ready');
        if (r.status === 'provisioning') return set_stage('awaiting');
        if (r.status === 'provisioning_failed') return set_stage('failed');
        if (r.status === 'pending_payment' && r.razorpay_subscription_id) {
          set_resume_info({ subdomain: r.subdomain, subscription_id: r.razorpay_subscription_id });
          return;
        }
        // No subscription on file or non-recoverable state — bail to plan.
        set_error('We could not resume this signup. Please start over.');
        set_stage('plan');
      } catch (err) {
        if (!cancelled) set_error(`Could not resume signup: ${friendly_error(err)}`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [stage, tenant_id]);

  // ── Load plans
  useEffect(() => {
    orpc.platform
      .list_plans({})
      .then((r) => {
        const list = r.plans as Plan[];
        set_plans(list);
        if (list[0]) set_plan_id(list[0].id);
      })
      .catch((err) => {
        set_error(`Could not load plans: ${friendly_error(err)}`);
      });
  }, []);

  // ── Subdomain availability
  useEffect(() => {
    if (!subdomain || subdomain.length < 3) {
      set_sub_check(null);
      return;
    }
    const h = setTimeout(() => {
      orpc.platform
        .check_subdomain({ subdomain })
        .then((r) => set_sub_check(r))
        .catch(() => set_sub_check(null));
    }, 400);
    return () => clearTimeout(h);
  }, [subdomain]);

  // ── Poll tenant_status while awaiting webhook confirmation
  useEffect(() => {
    if (stage !== 'awaiting' || !tenant_id) return;
    let cancelled = false;
    const tick = async (): Promise<void> => {
      try {
        const r = await orpc.platform.tenant_status({ tenant_id });
        if (cancelled) return;
        if (r.status === 'active') {
          set_stage('ready');
          return;
        }
        if (r.status === 'provisioning_failed') {
          set_error('Provisioning failed. Please contact support.');
          set_stage('failed');
          return;
        }
        // pending_payment | provisioning | suspended | past_due → keep polling
        setTimeout(tick, 3_000);
      } catch {
        // eslint-disable-line — transient network error during the wizard's
        // status-polling loop is recoverable; we back off and retry rather
        // than abort the signup. The next successful tick will surface any
        // real failure via the status === 'provisioning_failed' branch.
        if (cancelled) return;
        setTimeout(tick, 5_000);
      }
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [stage, tenant_id]);

  const selected_plan = plans.find((p) => p.id === plan_id) ?? null;
  const selected_price_paise = selected_plan
    ? billing_period === 'yearly'
      ? selected_plan.yearly_price_paise
      : selected_plan.monthly_price_paise
    : 0;

  // ── Step 3 submit → backend creates Razorpay subscription, then we open
  //    the Razorpay Checkout Standard modal in-page. The modal's `handler`
  //    fires when the mandate is authenticated → we flip to awaiting + the
  //    webhook does the rest of the work in the background.
  const submit_payment = async (): Promise<void> => {
    set_error(null);
    set_busy(true);
    try {
      const r = await orpc.platform.signup({
        business_name,
        subdomain,
        primary_email: email,
        ...(phone ? { primary_phone: phone } : {}),
        plan_id,
        billing_period,
        accepts_terms: true as const,
      });
      // Persist the tenant_id in the URL so a reload while waiting keeps
      // polling the right row.
      set_tenant_id(r.tenant_id);
      const next = new URLSearchParams(params);
      next.set('tenant_id', r.tenant_id);
      set_params(next, { replace: true });

      const plan_label = plans.find((p) => p.id === plan_id)?.label ?? 'Plan';
      await open_subscription_checkout({
        subscription_id: r.razorpay_subscription_id,
        name: 'ChitDesk',
        description: `${plan_label} · ${billing_period}`,
        prefill: {
          name: business_name,
          email,
          ...(phone ? { contact: phone } : {}),
        },
        on_success: () => {
          // Mandate authenticated. subscription.authenticated webhook is
          // racing in the background; flip to awaiting and let
          // tenant_status polling drive the UI to "ready".
          set_stage('awaiting');
        },
        on_dismiss: () => {
          // Operator closed the modal without authenticating. The tenant
          // row exists in pending_payment; they can click "Continue to
          // payment" again to reopen the modal against the same
          // subscription_id.
          set_busy(false);
          set_error('Payment was cancelled. Click "Continue to payment" to try again.');
        },
      });
    } catch (err) {
      set_error(friendly_error(err));
      set_busy(false);
    }
  };

  // Resume an in-flight signup: open Razorpay Checkout against the existing
  // pending subscription (no new tenant created). Same modal helper as the
  // first-time flow, so success → awaiting → webhook → ready.
  const submit_resume_payment = async (): Promise<void> => {
    if (!resume_info) return;
    set_error(null);
    set_busy(true);
    try {
      await open_subscription_checkout({
        subscription_id: resume_info.subscription_id,
        name: 'ChitDesk',
        description: 'Resume signup payment',
        on_success: () => {
          const next = new URLSearchParams(params);
          next.set('ok', '1');
          set_params(next, { replace: true });
          set_stage('awaiting');
        },
        on_dismiss: () => {
          set_busy(false);
          set_error('Payment was cancelled. Click "Continue to payment" to try again.');
        },
      });
    } catch (err) {
      set_error(friendly_error(err));
      set_busy(false);
    }
  };

  const handle_step2 = (e: FormEvent): void => {
    e.preventDefault();
    if (!accepts) {
      set_error('You must accept the terms.');
      return;
    }
    if (sub_check && !sub_check.ok) {
      set_error(`Subdomain ${sub_check.reason ?? 'unavailable'}`);
      return;
    }
    set_error(null);
    set_stage('pay');
  };

  const shell = (children: ReactElement, step?: number): ReactElement => (
    <main className="min-h-screen bg-bg text-ink flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-xl bg-surface border border-line rounded-lg p-8 shadow-sm">
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-line">
          <Link to="/" className="font-semibold tracking-tight text-2xl text-primary leading-none">ChitDesk</Link>
          {step !== undefined && (
            <span className="text-xs text-ink/50">Step {step} of 3</span>
          )}
        </div>
        {children}
      </div>
    </main>
  );

  // ── Step 1: Plan picker
  if (stage === 'plan') {
    return shell(
      <div>
        <h1 className="text-xl font-medium mb-1">Choose a plan</h1>
        <p className="text-sm text-ink/60 mb-4">You can change plans anytime.</p>

        <div className="flex items-center justify-center gap-1 mb-6 bg-bg-elev rounded p-1 text-sm">
          {(['monthly', 'yearly'] as const).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => set_billing_period(p)}
              className={`px-4 py-1.5 rounded transition ${
                billing_period === p
                  ? 'bg-primary text-white font-medium'
                  : 'text-ink/60 hover:text-ink'
              }`}
            >
              {p === 'monthly' ? 'Monthly' : 'Yearly · save 2 months'}
            </button>
          ))}
        </div>

        {plans.length === 0 && (
          <div className="text-sm text-ink/50 text-center py-8">Loading plans…</div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {plans.map((p) => {
            const active = p.id === plan_id;
            const price = billing_period === 'yearly' ? p.yearly_price_paise : p.monthly_price_paise;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => set_plan_id(p.id)}
                className={`text-left border rounded-lg p-4 transition ${
                  active ? 'border-primary bg-primary-soft' : 'border-line hover:border-ink/30'
                }`}
              >
                <div className="text-sm font-medium text-primary">{p.label}</div>
                <div className="text-2xl mt-2 font-semibold">
                  ₹{format_inr(price)}
                  <span className="text-xs font-normal text-ink/50">
                    {' '}
                    /{billing_period === 'yearly' ? 'yr' : 'mo'}
                  </span>
                </div>
                {p.description && (
                  <div className="text-xs text-ink/60 mt-2">{p.description}</div>
                )}
                {p.features.length > 0 && (
                  <ul className="text-xs text-ink/60 mt-2 space-y-0.5">
                    {p.features.slice(0, 4).map((f, i) => (
                      <li key={i}>• {f}</li>
                    ))}
                  </ul>
                )}
              </button>
            );
          })}
        </div>

        {error && <div className="mt-4 px-3 py-2 bg-bad-soft text-bad text-xs rounded">{error}</div>}

        <div className="mt-6 flex justify-between text-sm">
          <Link to="/" className="text-ink/60 hover:text-primary">← Cancel</Link>
          <button
            type="button"
            onClick={() => set_stage('details')}
            disabled={!plan_id}
            className="bg-primary hover:bg-primary-2 disabled:bg-ink/20 text-white px-4 py-2 rounded font-medium"
          >
            Next →
          </button>
        </div>
      </div>,
      1,
    );
  }

  // ── Step 2: Business details
  if (stage === 'details') {
    return shell(
      <form onSubmit={handle_step2}>
        <h1 className="text-xl font-medium mb-1">Your business</h1>
        <p className="text-sm text-ink/60 mb-6">
          We'll send a welcome link to your email once payment is confirmed.
        </p>

        <div className="flex flex-col gap-4">
          <label className="text-sm">
            <span className="block mb-1">Business name</span>
            <input
              type="text"
              value={business_name}
              onChange={(e) => set_business_name(e.target.value)}
              placeholder="Ramesh Chits"
              minLength={2}
              maxLength={120}
              required
              className="w-full px-3 py-2 border border-line rounded bg-bg-elev text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </label>

          <label className="text-sm">
            <span className="block mb-1">Subdomain</span>
            <div className="flex items-center gap-1">
              <input
                type="text"
                value={subdomain}
                onChange={(e) => set_subdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                placeholder="rameshchits"
                minLength={3}
                maxLength={30}
                pattern="[a-z][-a-z0-9]*[a-z0-9]"
                required
                className="flex-1 px-3 py-2 border border-line rounded bg-bg-elev font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
              <span className="text-xs text-ink/50 font-mono">.{APEX}</span>
            </div>
            {sub_check && (
              <span className={`text-xs mt-1 block ${sub_check.ok ? 'text-good' : 'text-bad'}`}>
                {sub_check.ok ? '✓ available' : `✗ ${sub_check.reason ?? 'unavailable'}`}
              </span>
            )}
          </label>

          <label className="text-sm">
            <span className="block mb-1">Email <span className="text-ink/40">· welcome link goes here</span></span>
            <input
              type="email"
              value={email}
              onChange={(e) => set_email(e.target.value)}
              placeholder="you@example.com"
              required
              className="w-full px-3 py-2 border border-line rounded bg-bg-elev text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </label>

          <label className="text-sm">
            <span className="block mb-1">Phone <span className="text-ink/40">(optional)</span></span>
            <input
              type="tel"
              value={phone}
              onChange={(e) => set_phone(e.target.value)}
              placeholder="+91 9876543210"
              pattern="\+91[6-9]\d{9}"
              className="w-full px-3 py-2 border border-line rounded bg-bg-elev font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </label>

          <label className="text-xs flex items-start gap-2 mt-1">
            <input
              type="checkbox"
              checked={accepts}
              onChange={(e) => set_accepts(e.target.checked)}
              className="mt-0.5"
            />
            <span>I accept the terms of service and privacy policy.</span>
          </label>
        </div>

        {error && <div className="mt-4 px-3 py-2 bg-bad-soft text-bad text-xs rounded">{error}</div>}

        <div className="mt-6 flex justify-between text-sm">
          <button type="button" onClick={() => set_stage('plan')} className="text-ink/60 hover:text-primary">
            ← Back
          </button>
          <button
            type="submit"
            disabled={!accepts || (sub_check !== null && !sub_check.ok)}
            className="bg-primary hover:bg-primary-2 disabled:bg-ink/20 text-white px-4 py-2 rounded font-medium"
          >
            Next →
          </button>
        </div>
      </form>,
      2,
    );
  }

  // ── Step 3: Payment summary → Razorpay redirect
  if (stage === 'pay') {
    const price_label = `₹${format_inr(selected_price_paise)}`;
    const period_label = billing_period === 'yearly' ? 'yearly' : 'monthly';
    return shell(
      <div>
        <h1 className="text-xl font-medium mb-1">Payment</h1>
        <p className="text-sm text-ink/60 mb-6">
          {selected_plan?.label} plan · billed {period_label} · UPI AutoPay
        </p>

        <div className="border border-line rounded-lg p-6 bg-bg-elev text-center">
          <div className="text-3xl font-semibold">
            {price_label}
            <span className="text-sm font-normal text-ink/50">
              {' '}
              /{billing_period === 'yearly' ? 'year' : 'month'}
            </span>
          </div>
          <p className="text-xs text-ink/60 mt-3">
            Auto-debited each {period_label === 'yearly' ? 'year' : 'month'} via UPI AutoPay.
            Cancel anytime from your workspace.
          </p>
          {String(import.meta.env.VITE_RAZORPAY_KEY_ID ?? '').startsWith('rzp_test') && (
            <div className="mt-4 text-xs text-warn bg-warn-soft inline-block px-2 py-1 rounded">
              ⚠ Razorpay test mode — use any test UPI like <code className="font-mono">success@razorpay</code>
            </div>
          )}
        </div>

        {error && <div className="mt-4 px-3 py-2 bg-bad-soft text-bad text-xs rounded">{error}</div>}

        <div className="mt-6 flex flex-col gap-3">
          <button
            type="button"
            onClick={submit_payment}
            disabled={busy}
            className="bg-primary hover:bg-primary-2 disabled:bg-ink/20 text-white py-3 rounded font-medium"
          >
            {busy ? 'Redirecting to Razorpay…' : `Continue to payment →`}
          </button>
          <button
            type="button"
            onClick={() => set_stage('details')}
            disabled={busy}
            className="text-xs text-ink/60 hover:text-primary self-start"
          >
            ← Back
          </button>
        </div>
      </div>,
      3,
    );
  }

  // ── Resume payment: tenant exists in pending_payment, reopen Razorpay
  //    against the same subscription (the create step is NOT repeated).
  if (stage === 'resume_pay') {
    return shell(
      <div>
        <h1 className="text-xl font-medium mb-1">Resume payment</h1>
        <p className="text-sm text-ink/60 mb-4">
          Continuing your signup{resume_info ? ` for `: '…'}
          {resume_info && (
            <span className="font-mono">{resume_info.subdomain}.{APEX}</span>
          )}
        </p>
        {!resume_info ? (
          <div className="text-sm text-ink/60">Loading subscription…</div>
        ) : (
          <>
            <div className="bg-bg-elev border border-line rounded-lg p-4 mb-4 text-sm text-ink/70">
              We held your tenant in pending payment. Click below to reopen
              Razorpay Checkout against the same subscription — no charge yet.
            </div>
            {error && (
              <div className="mb-3 px-3 py-2 bg-bad-soft text-bad text-xs rounded">{error}</div>
            )}
            <button
              type="button"
              onClick={() => void submit_resume_payment()}
              disabled={busy}
              className="w-full bg-primary hover:bg-primary-2 disabled:bg-ink/20 text-white px-4 py-3 rounded font-medium"
            >
              {busy ? 'Opening Razorpay…' : 'Continue to payment →'}
            </button>
            <button
              type="button"
              onClick={() => {
                set_resume_info(null);
                set_tenant_id('');
                const next = new URLSearchParams(params);
                next.delete('tenant_id');
                next.delete('ok');
                set_params(next, { replace: true });
                set_error(null);
                set_stage('plan');
              }}
              className="mt-3 text-sm text-ink/50 hover:text-ink underline"
            >
              ← Start over
            </button>
          </>
        )}
      </div>,
      3,
    );
  }

  // ── Awaiting webhook confirmation (mandate just authenticated)
  if (stage === 'awaiting') {
    return shell(
      <div>
        <h1 className="font-tamil text-2xl text-primary">பணம் சரிபார்க்கப்படுகிறது…</h1>
        <p className="text-sm text-ink/60 mt-1 mb-6">
          UPI AutoPay confirmed. Setting up your workspace now.
        </p>

        <ul className="text-sm space-y-2">
          <li className="flex items-center gap-3">
            <span className="text-good">✓</span> UPI AutoPay authenticated
          </li>
          <li className="flex items-center gap-3">
            <span className="animate-pulse text-primary">●</span> Provisioning your workspace…
          </li>
          <li className="flex items-center gap-3 text-ink/40">
            <span>○</span> Sending welcome email
          </li>
        </ul>

        <p className="text-xs text-ink/50 mt-6">
          This usually takes 30–90 seconds. You can safely leave this page — we'll email
          you the welcome link when your workspace is ready.
        </p>
      </div>,
    );
  }

  // ── Ready
  if (stage === 'ready') {
    return shell(
      <div>
        <h1 className="font-tamil text-3xl text-primary mb-1">தயார்!</h1>
        <p className="text-sm text-ink/60 mb-6">Your workspace is ready.</p>

        <p className="text-sm">
          We've sent a welcome link to{' '}
          <b className="font-mono">{email || 'your inbox'}</b>. Open it to set your
          password and sign in.
        </p>

        <p className="text-xs text-ink/50 mt-4">Don't see it after a minute? Check spam.</p>
      </div>,
    );
  }

  // ── Failed
  if (stage === 'failed') {
    return shell(
      <div>
        <h1 className="text-xl font-medium mb-2">Something went wrong</h1>
        <p className="text-sm text-ink/70 mb-6">
          Payment or provisioning failed. No charge was captured.
        </p>
        {error && <div className="mb-4 px-3 py-2 bg-bad-soft text-bad text-xs rounded">{error}</div>}
        <button
          type="button"
          onClick={() => {
            set_error(null);
            set_tenant_id('');
            set_stage('plan');
            const next = new URLSearchParams(params);
            next.delete('tenant_id');
            set_params(next, { replace: true });
          }}
          className="bg-primary hover:bg-primary-2 text-white px-4 py-2 rounded font-medium text-sm"
        >
          Start over
        </button>
      </div>,
    );
  }

  return shell(<div>Loading…</div>);
}
